/**
 * The panel's clock vocabulary. Every money and usage figure must be labelled with one of these.
 *
 * THE PROBLEM THIS EXISTS TO KILL. Three different spans coexist in this module — the selected
 * reporting range (7/15/30 days), the budget cycle LiteLLM enforces `budget_usd` against, and
 * all-time — and figures were being labelled "Total spend", "Spend", "Window spend" and
 * "Spend · period" more or less interchangeably. The result was that Overview's "Total spend" (the
 * range) and Analysis's "Total spend" (the cycle) were two different numbers under one phrase, and
 * an admin reading both tabs had no way to reconcile them.
 *
 * The rule: a figure's own LABEL carries its clock. Not the hint line underneath it — a label that
 * needs a footnote to be unambiguous is ambiguous. `Spend · 30 days` and `Spend · this cycle` are
 * the only two shapes; "window" and "period" are banished from visible copy and from aria labels,
 * because a reader should not have to know what this product means by those words.
 *
 * WHY THERE ARE NO RESOLVED CALENDAR DATES HERE. Showing "3 Aug – 1 Sep" beside "last 30 days" is
 * the right UX and is deliberately absent: `OrgUsageReport` carries `window_days` and no start or
 * end timestamp, so the boundary — inclusive of today or not, UTC or local, midnight or now — is
 * unknown. Deriving it would put a precise, confident, possibly wrong date range on a billing
 * screen. Add this the day the report reports its own bounds.
 */
export const CYCLE_CLOCK = 'this cycle' as const;

/** Suffix for a figure scoped to the selected reporting range. */
export function rangeClock(days: number): string {
  return `${days} days`;
}

/** `Spend · 30 days`, `Tokens · 7 days` — a measure labelled with the selected range. */
export function labelForRange(measure: string, days: number): string {
  return `${measure} · ${rangeClock(days)}`;
}

/** `Spend · this cycle` — a measure labelled with the budget cycle. */
export function labelForCycle(measure: string): string {
  return `${measure} · ${CYCLE_CLOCK}`;
}

export function formatTokenCount(n: number): string {
  if (!n) return '0';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

/*
 * Two decimals MINIMUM, four MAXIMUM — not a flat four.
 *
 * Spend here is genuinely sub-cent: a run's cost reconciles against LiteLLM's
 * `/spend/logs` to 8 decimal places, and rounding to 2 threw that away at the
 * point a reader is trying to check it. Measured on dev 2026-09-11: the Usage
 * tab read "$1.56" against an events total of $1.55846944 and a LiteLLM counter
 * of $1.5718689809 — two figures a cent apart rendered as the same string, which
 * is exactly the gap a reader was being asked to investigate.
 *
 * The maximum is 4 rather than 8 because that is where the Observability tab
 * already sits ($1.0159), so the two tabs now round the same way.
 *
 * The minimum stays 2 so amounts that ARE round keep their familiar shape: a
 * $20.00 budget and a $49.00 plan price must not become $20 and $49, and a flat
 * `maximumFractionDigits: 4` would have rendered them $20.0000. This formatter is
 * shared with budgets, credits, invoices and plan prices — money you agree to pay,
 * which is priced in cents — so it has to read correctly for both.
 */
export function formatCurrencyUsd(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

// Mirrors the backend's own definition (UsageContext.normalize/2: `spend >= budget`).
// Paired with spend_usd_cycle, not spend_usd_window or spend_usd_lifetime — the
// budget cycle, the report's selected day window, and all-time are three different
// clocks (see usage-api.md caveats). spend_usd_cycle is the one LiteLLM actually
// enforces budget_usd against; spend_usd_lifetime deliberately survives a reset,
// so it no longer reflects current budget standing.
export function isOverBudget(spendUsdCycle: number, budgetUsd: number): boolean {
  return spendUsdCycle >= budgetUsd;
}

// Raw slugs as sent by product services ingesting into observability_events —
// the API returns them as-is, no friendly-label mapping happens server-side.
const SERVICE_LABELS: Record<string, string> = {
  agents: 'Agents',
  playground: 'Migration',
  agentic_platform: 'Agentic Platform',
  'multi-step-agentic': 'Weave / Multi-Agent',
  testing: 'Testing',
  'release-scanner': 'Release Scanner',
  // Underscore spellings the backend also emits for the same two services. Observed in a live
  // Analysis chart rendering the raw slugs `testing_service` and `release_scanner` beside a correctly
  // labelled "Weave / Multi-Agent", so these are aliases of existing entries, not new services.
  testing_service: 'Testing',
  release_scanner: 'Release Scanner',
};

/**
 * A readable last resort for a slug this build has no curated label for.
 *
 * Formatting, not renaming: separators become spaces and each word is capitalised, so an unmapped
 * `security_scanner` reads as "Security Scanner" instead of leaking the raw identifier into a chart
 * axis. It never invents a different name for a service — if the curated label matters, add it above.
 */
function humaniseServiceSlug(service: string): string {
  return service
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function labelForService(service: string): string {
  return SERVICE_LABELS[service] ?? humaniseServiceSlug(service);
}
